import { ArrowDropDown, ArrowDropUp, Clear, ExpandLess, ExpandMore } from '@mui/icons-material';
import { createTheme, IconButton, Popover, PopoverActions, Stack, TextField, ThemeProvider, Tooltip, Typography } from '@mui/material';
import { subscribe, unsubscribe } from 'pubsub-js';
import {  useEffect, useMemo, useRef, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectChangedMsg, OBJECT_CHANGED_TOPIC } from '../../common/models/pub-sub/PubSubTopics';
import { useAppSettings } from '../../contexts/app-context/AppContext';
import { useUserContext } from '../../contexts/user-context/UserContext';
import { useGroup } from '../../queries/groups/UseGroup';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { INavigationItem } from '../../services/navigation-data-service/Models';
import GroupHierarchy from '../group-hierarchy/desktop/GroupHierarchy';
import MobileGroupPicker from './MobileGroupPicker';
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import { sleep } from '../../common/utilities/Sleep';


export type GroupPickerProps = {
    groupID: number | undefined;
    onSelected: (item: INavigationItem) => void;
    hiddenGroups?: number[],
    showFullPath?: boolean,
    rootID?: number,
    vertical?: 'top' | 'center' | 'bottom' | number;
    horizontal?: 'left' | 'center' | 'right' | number;
    label?: string;
    textFieldLabel?: boolean
    helperText?: string;
    placeholder?: string;
    allowClear?: boolean;
    onClear?: () => void;
    
  
};

export default function GroupPicker({
    groupID,
    onSelected,
    hiddenGroups,
    showFullPath,
    rootID,
    vertical,
    horizontal,
    label,
    textFieldLabel,
    helperText,
    placeholder,
    allowClear,
    onClear
}: GroupPickerProps) {

    const isPhone = isMobile && !isTablet;
    const [popOverAnchorEl, setPopOverAnchorEl] = useState<HTMLElement | undefined>(); 
    const routeContext = useRouteContext();
    const [appSettings] = useAppSettings();
    const [userState] = useUserContext();
    const { data: group, refetch } = useQueryNotifications(useGroup)(
    {
        customerID: userState.customer?.id as number, 
        groupID: groupID ?? routeContext.groupID!
    });

    const mainTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );

    // const theme = useMemo(
    //     () => {
    //         return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2) :
    //             createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    //     },
    //     [appSettings.themeSet,
    //         isPhone]
    // );

    const openPopOver = Boolean(popOverAnchorEl);
    const id = openPopOver ? 'grg-popover' : undefined;        

    const popoverRef = useRef<PopoverActions>(null);


    const onShowPopOver = (event: React.MouseEvent<HTMLElement>) => {
        setPopOverAnchorEl(event.currentTarget);
    };
    
    const onClosePopOver = () => {
        setPopOverAnchorEl(undefined);
    };

    const onGroupChanged = (item: INavigationItem) => {
        onSelected(item);
        onClosePopOver();
    }

    const onClosePicker = (item?: INavigationItem) => {

        if (item) {
            onSelected(item);
        }

        onClosePopOver();
    }


    useEffect(() => {
        const onObjectChanged = (objectChangedData: ObjectChangedMsg) => {

            // the changed object is a group and the name is not set, then the change likely came from
            // group admin so rebuild the breadcrumb
            if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group) {
                refetch();
            }
        }

        const token = subscribe(OBJECT_CHANGED_TOPIC, (message, data) => onObjectChanged(data));

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }

    }, [
        refetch
    ]);

    useEffect(() => {


        const updatePosition = async () => {

            let count = 3;
            while (count--) {
                await sleep(100);
                popoverRef?.current?.updatePosition();
            }
        }

        if (popOverAnchorEl && popoverRef.current && textFieldLabel) {
            updatePosition();
        }
    }, [
        popOverAnchorEl,
        textFieldLabel

    ]);

    return (
        <>
            <Stack direction='row' 
                spacing={1} 
                onClick={(e) => onShowPopOver(e)} 
                sx={{
                    cursor: 'pointer', 
                    overflow: 'hidden', 
                    alignItems: "center",
                    flexBasis: '100%',
                    flex: '1 1 auto'
              }}>


                {!textFieldLabel ? 
                <Tooltip title={group?.fullPath ?? ''}>                        
                    <Typography
                        variant={isPhone ? 'body1' : 'body2'}
                        component="div"
                        sx={{
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            paddingLeft: theme => theme.spacing(isPhone ? 7 : showFullPath ? 0 : 3),
                            marginTop: 'auto',
                            marginBottom: 'auto'
                        }}>
                        {showFullPath ? group?.fullPath : group?.title}
                    </Typography>
                </Tooltip> :
                <TextField
                    variant="filled"
                    id={id}
                    label={label}
                    helperText={helperText}
                    placeholder={placeholder}
                    value={!groupID ? '' : group?.title ?? ''}
                    sx={{
                        paddingRight: 0,
                        width: '100%',
                        cursor: 'pointer',
                        "& input": {
                            cursor: 'pointer'  
                        }
                    }}
                    slotProps={{
                        input: {
                            readOnly: true,
                            style: {
                                paddingRight: 0,
                                width: '100%',
                            },
                            
                            endAdornment: <Stack direction='row'>
                                <IconButton>
                                {openPopOver ? 
                                    <ArrowDropUp /> : 
                                    <ArrowDropDown />
                                }
                                </IconButton>
                                {allowClear ? 
                                    <IconButton
                                        onClick={(e) =>{ 

                                            e.stopPropagation();
                                            onClear && onClear();
                                        }
                                        }>
                                        <Clear />
                                    </IconButton> : null}
                                
                                </Stack>,
                        },

                        inputLabel: {
                            shrink: true,

                        }
                    }} />            
                }

            {!textFieldLabel && <IconButton
                // sx={{ color: isPhone ? '#fff' : undefined }}
                sx={{ color: isPhone ? '#fff' : "inherit" }}
                aria-label="related"
                aria-controls="group-menu"
                aria-haspopup="true"
                >
                {openPopOver ? <ExpandLess /> : <ExpandMore /> }
            </IconButton>
            }

            </Stack>
            {!isPhone ? 
            <ThemeProvider theme={mainTheme}>
                <Popover 
                    id={id}
                    open={openPopOver}
                    anchorEl={popOverAnchorEl}
                    onClose={onClosePopOver}
                    anchorOrigin={{
                        vertical: vertical ?? 'bottom',
                        horizontal: horizontal ?? 'left',
                    }}
                    transformOrigin={{
                        vertical: vertical ?? 'bottom',
                        horizontal: horizontal ?? 'left',
                    }}

                    action={popoverRef}            

                    sx={{maxWidth: 800}}
                >
                    <GroupHierarchy 
                        onSelected={onGroupChanged} 
                        defaultSelected={groupID} 
                        hiddenGroups={hiddenGroups}
                        rootID={rootID} 
                        onExpanded={() => {
                            if (textFieldLabel) {
                                // KG 2025-03-10 - this is a hack to force the popover to reposition after the tree is expanded
                                setTimeout(() => {

                                    popoverRef?.current?.updatePosition();    
                                }, 200);
                            }
                        }}
                        />
                </Popover>
            </ThemeProvider>

            :
            <MobileGroupPicker 
                        open={openPopOver}
                        onClose={onClosePicker} 
                        groupID={groupID} 
                        rootID={rootID} />
            
            }
        </>
    );
  }



